import { toRelayDateTime } from './relay';
import type { Store } from './port';
import type { DateiuebertragungRow } from './rows';

/**
 * Der Übertragungsstand der abgelegten Dateien.
 *
 * **Kein Eintrag heisst „offen".** Eine Zeile entsteht erst beim ersten Versuch; das Ablegen
 * einer Aufnahme (`platform/storage.ts`) bleibt damit frei von jeder Kenntnis über die
 * Übertragung, und eine eben entstandene Aufnahme gilt von selbst als ausstehend.
 *
 * Dieses Modul rechnet und speichert, es sendet nicht — wer sendet, steht in
 * `server/anhaenge.ts`. Die Trennung ist der Grund, warum sich der Rückzug hier ohne Netz und
 * ohne Uhr prüfen lässt.
 */

/**
 * Nach so vielen **Ablehnungen** wird eine Datei nicht mehr geschickt.
 *
 * ⚠️ **Ablehnungen, nicht Fehlschläge.** Der Rückzug deckelt bei 60 Sekunden; zählte man jeden
 * Fehlversuch, wäre eine Datei nach viereinhalb Minuten ohne Netz für immer aufgegeben — genau
 * in der Lage, in der Wiederholen richtig ist. Ein 4xx dagegen ist eine überlegte Antwort des
 * Servers; fünfmal dieselbe zu hören genügt.
 *
 * Fünf und nicht drei: der erste Versuch kann an einer halb angemeldeten Sitzung scheitern,
 * und ein Server, der gerade neu startet, antwortet mitunter kurz mit 4xx.
 */
export const ABLEHNUNGSGRENZE = 5;

/**
 * Zählt dieser Status auf `ABLEHNUNGSGRENZE`? Nur ein 4xx, das über **die Datei** urteilt.
 *
 * ⚠️ 401/403 (Sitzung weg, etwa nach einem Serverneustart), 404 (Pfad gerade nicht da), 408
 * und 429 (Server überlastet) sagen nichts über die Datei. Zählten sie mit, wäre nach einem
 * Neustart von Relay jede wartende Aufnahme nach einer Minute für immer aufgegeben
 * (Audit 2026-10-11, Fund 5). Gilt für Relay und Classic gleich.
 */
export function istAblehnung(status: number): boolean {
  return status >= 400 && status < 500 && ![401, 403, 404, 408, 429].includes(status);
}

/**
 * Der Rückzug nach `versuche` Fehlschlägen: 1 s, 2 s, 4 s … höchstens 60 s.
 *
 * Der Deckel ist kein Zierrat: ohne ihn stünde eine Datei nach zwölf Fehlschlägen über eine
 * Stunde still, und ein Prüfungstag hat keine Stunde übrig.
 */
export function rueckzugMs(versuche: number): number {
  if (versuche <= 0) return 0;
  return Math.min(2 ** (versuche - 1) * 1000, 60000);
}

/**
 * Alle Zeilen, nach Pfad greifbar.
 *
 * Ein Übertragungslauf liest sie **einmal** statt je Datei einzeln: bei dreihundert Aufnahmen
 * wären das sonst dreihundert Abfragen für eine Auskunft, die sich in einem Zug beschaffen
 * lässt.
 */
export async function staende(store: Store): Promise<Map<string, DateiuebertragungRow>> {
  const zeilen = await store.all<DateiuebertragungRow>('Dateiuebertragung');
  return new Map(zeilen.map((zeile) => [zeile.id, zeile]));
}

/**
 * Ob der Server diese Datei so oft abgewiesen hat, dass sie nicht mehr geschickt wird.
 *
 * **Abgeleitet, nicht gespeichert:** ein eigener Merker wäre eine zweite Wahrheit neben
 * `ablehnungen` und liefe irgendwann auseinander. Eine erledigte Datei ist nie aufgegeben —
 * sie ist angekommen.
 *
 * `?? 0` für Sätze aus einer Ablage vor Fassung 4: im Browser kann das Feld fehlen, und ein
 * Vergleich mit `undefined` ist immer falsch — die Datei ginge dann ewig weiter hinaus.
 */
export function istAufgegeben(zeile: DateiuebertragungRow | undefined): boolean {
  if (!zeile || zeile.erledigt === 'Y') return false;
  return (zeile.ablehnungen ?? 0) >= ABLEHNUNGSGRENZE;
}

/**
 * Ob eine Datei jetzt an der Reihe ist.
 *
 * Vier Fälle: keine Zeile (noch nie versucht) → ja. Erledigt → nie wieder. Zu oft abgewiesen →
 * ebenfalls nie wieder. Sonst erst, wenn der Rückzug abgelaufen ist.
 *
 * Verglichen wird als Zeichenkette, nicht als Zahl — `char(17)` ist so gebaut, dass die
 * lexikografische Ordnung die zeitliche ist.
 */
export function istFaellig(zeile: DateiuebertragungRow | undefined, jetzt: Date): boolean {
  if (!zeile) return true;
  if (zeile.erledigt === 'Y') return false;
  if (istAufgegeben(zeile)) return false;
  if (!zeile.naechsterVersuch) return true;
  return zeile.naechsterVersuch <= toRelayDateTime(jetzt);
}

/**
 * Die Datei unter diesem Pfad ist eine **andere** geworden — der Übertragungsstand gilt nicht
 * mehr.
 *
 * Genau ein Fall in der App schreibt eine bestehende Datei um: die Markierungsebene eines
 * Fotos (`platform/storage.ts::saveTextAs`, gerufen aus `features/annotate/annotateSave.ts`).
 * Sie behält ihren Namen mit Absicht — ein neuer je Fassung kappte den Bezug zum Bild.
 *
 * ⚠️ **Ohne diesen Griff bliebe die alte Fassung auf dem Server stehen.** `merkeErfolg` setzt
 * `erledigt='Y'`, und `istFaellig` schickt eine erledigte Datei nie wieder. Der Prüfer sähe
 * am Gerät seine geänderten Markierungen, in der Auswertung stünde die Fassung von vorher —
 * und nichts wiese darauf hin.
 *
 * Die Zeile wird **gelöscht**, nicht zurückgesetzt: keine Zeile heisst „offen, noch nie
 * versucht", und genau das ist der Stand einer neu geschriebenen Datei. Damit fallen auch die
 * Ablehnungen weg — die galten dem alten Inhalt.
 */
export async function oeffneErneut(store: Store, pfad: string): Promise<void> {
  await store.remove('Dateiuebertragung', { id: pfad });
}

/** Die Datei ist beim Server angekommen. */
export async function merkeErfolg(
  store: Store,
  args: { pfad: string; examID: string | null; art: string; jetzt?: Date },
): Promise<void> {
  const jetzt = args.jetzt ?? new Date();
  const vorher = (await staende(store)).get(args.pfad);
  await store.put<DateiuebertragungRow>('Dateiuebertragung', [
    {
      id: args.pfad,
      examID: args.examID,
      art: args.art,
      erledigt: 'Y',
      storeDate: toRelayDateTime(jetzt),
      // Die Zahl der Versuche bleibt stehen: sie ist im Nachhinein die einzige Spur davon,
      // wie mühsam diese eine Datei war.
      versuche: vorher?.versuche ?? 0,
      ablehnungen: vorher?.ablehnungen ?? 0,
      naechsterVersuch: null,
      letzterFehler: null,
    },
  ]);
}

/**
 * Der Versuch ging schief; die Datei bleibt liegen und rückt ihren nächsten Anlauf hinaus.
 *
 * `abgewiesen` trennt die zwei Sorten Fehlschlag: der Server hat geantwortet und abgelehnt
 * (4xx) — oder es kam gar keine Antwort (kein Netz, Zeitablauf, 5xx). Nur das erste zählt auf
 * die Obergrenze; das zweite darf beliebig oft geschehen, ohne dass etwas aufgegeben wird.
 */
export async function merkeFehlschlag(
  store: Store,
  args: {
    pfad: string;
    examID: string | null;
    art: string;
    fehler: string;
    jetzt?: Date;
    abgewiesen?: boolean;
  },
): Promise<void> {
  const jetzt = args.jetzt ?? new Date();
  const vorher = (await staende(store)).get(args.pfad);
  const versuche = (vorher?.versuche ?? 0) + 1;
  await store.put<DateiuebertragungRow>('Dateiuebertragung', [
    {
      id: args.pfad,
      examID: args.examID,
      art: args.art,
      erledigt: 'N',
      storeDate: vorher?.storeDate ?? null,
      versuche,
      ablehnungen: (vorher?.ablehnungen ?? 0) + (args.abgewiesen ? 1 : 0),
      naechsterVersuch: toRelayDateTime(new Date(jetzt.getTime() + rueckzugMs(versuche))),
      // Nur die erste Zeile und höchstens 200 Zeichen: eine Fehlermeldung mit ganzem
      // Antwortrumpf bläht die Ablage, und mehr als den Grund braucht hier niemand.
      letzterFehler: args.fehler.split('\n')[0].slice(0, 200),
    },
  ]);
}
